import { Grid, Typography, Tooltip, Box } from '@mui/material';

import { useDispatch } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import NotificationStatusIcon from 'components/Notification/NotificationStatusIcon';

import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import { MODULES } from 'common/constants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { setRequirementInventoryBreadcrumbText } from 'common/store/reducer/commonReducer';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';
import { dateFullYearFormat, urlWithTenant } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { IGroupsTableList } from 'features/RegulationInventory/api/type-def';

import styles from './CompleteTab.module.scss';

const { regulationInventory, overView, regulationMappingAssessment } =
	rrcpDashboardRoutes;

const { REQUIREMENT_INVENTORY } = MODULES;

interface IGroupsTableProps {
	id?: string;
	isLoading: boolean;
	inventoryTableData: IGroupsTableList[];
	getTableData: any;
	totalRecordsCount: number;
	errorMessage: string;
}

const CompleteTabTable = (props: IGroupsTableProps) => {
	const {
		id,
		isLoading,
		inventoryTableData,
		getTableData,
		totalRecordsCount,
		errorMessage,
	} = props;

	const { tableQueryObj, updateTableQuery } = useTableQuery({ id });
	const { updateQueryFlag } = useTableQuery({ id: 'AllRegTable' });

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const navigateToMappingDetails = (params: IGroupsTableList) => {
		dispatch(setWorkflowFeedback([]));
		updateQueryFlag(true);
		dispatch(setRequirementInventoryBreadcrumbText(REQUIREMENT_INVENTORY));
		navigate(
			urlWithTenant(
				`/${regulationInventory}/${overView}/${regulationMappingAssessment}/${params.groupId}`,
			),
		);
	};

	const getAssociatedBusinessNames = (param: IGroupsTableList) =>
		param?.businessUnitDetailDTOS?.map((res: any) => {
			const element = JSON.parse(res);
			return {
				id: element.businessUnitId,
				name: `${element.businessUnitName || '-'}`,
			};
		}) || [];

	const getAssociatedAssignedUser = (param: IGroupsTableList) =>
		param?.businessUnitDetailDTOS?.map((res: any) => {
			const element = JSON.parse(res);
			return {
				id: element.businessUnitId,
				name: `${element.assignedUserName || '-'}`,
			};
		}) || [];

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			getTableData(data);
		}
	};

	const handleSortChange = (data: any) => {
		getTableData(data);
		updateTableQuery(data);
	};

	const columns: readonly Column[] = [
		{
			id: 'groupName',
			label: 'Obligations',
			isSortable: true,
			disablePadding: true,
			cellClassName: styles['complete-tab-text-icon-wrpper'],
			render: (param: IGroupsTableList) => (
				<div className={styles.gridContainer}>
					<Typography
						className={styles['complete-tab-text-section']}
						data-testid="theme-link"
						onClick={() => navigateToMappingDetails(param)}
					>
						<Tooltip
							className={styles['complete-tab-text-value-section']}
							title={param.groupName}
						>
							<span>{param.groupName}</span>
						</Tooltip>
						<NotificationStatusIcon
							date={param?.reviewByDate}
							className={styles['complete-tab-icon-date']}
						/>
					</Typography>
				</div>
			),
		},
		{
			id: 'business Unit',
			label: 'Business Unit',
			disablePadding: true,
			isSortable: false,
			render: (param: IGroupsTableList) => (
				<div className={styles['text-multi-select-values']}>
					{getAssociatedBusinessNames(param).map((item: any, index: any) => {
						if (index === 0) {
							return (
								<Typography className={styles['text-down']} key={item.id}>
									{item.name}
								</Typography>
							);
						}
						return (
							<Typography className={styles['text-down']} key={item.id}>
								<br />
								{item.name}
							</Typography>
						);
					})}
				</div>
			),
		},
		{
			id: 'Assigned to',
			label: 'Assigned to',
			disablePadding: true,
			isSortable: false,
			render: (param: IGroupsTableList) => (
				<div className={styles['text-multi-select-values']}>
					{getAssociatedAssignedUser(param).map((item: any, index: any) => {
						if (index === 0) {
							return (
								<Typography className={styles['text-down']} key={item.id}>
									{item.name}
								</Typography>
							);
						}
						return (
							<Typography className={styles['text-down']} key={item.id}>
								<br />
								{item.name}
							</Typography>
						);
					})}
				</div>
			),
		},
		{
			id: 'createdByName',
			label: 'Created By',
			disablePadding: true,
			isSortable: true,
			render: (param: IGroupsTableList) => (
				<Typography className={styles['complete-tab-text-section-common']}>
					{param?.createdByName}
				</Typography>
			),
		},
		{
			id: 'taskStatus',
			label: 'Status',
			disablePadding: true,
			isSortable: true,
			render: (param: IGroupsTableList) => (
				<Typography className={styles['complete-tab-text-section-common']}>
					{param?.taskStatus}
				</Typography>
			),
		},
		{
			id: 'priority',
			label: 'Priority',
			disablePadding: true,
			isSortable: true,
			render: (param: IGroupsTableList) => (
				<Typography className={styles['complete-tab-text-section-common']}>
					{param?.priority}
				</Typography>
			),
		},
		{
			id: 'reviewByDate',
			label: 'Due Date',
			disablePadding: false,
			isSortable: true,
			render: (param: any) => (
				<Typography className={styles['complete-tab-text-section-common']}>
					{dateFullYearFormat(param?.reviewByDate || '')}
				</Typography>
			),
		},
	];

	return (
		<Box data-testid="allregulations-main-box">
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<Box className={styles['complete-tab-table-area']}>
					<TableComponent
						defaultQuery={tableQueryObj}
						rows={inventoryTableData}
						columns={columns}
						totalItems={totalRecordsCount}
						isCheckBoxEnabled={false}
						defaultSortKey="reviewByDate"
						sortDirection="desc"
						rowHoverEnabled={false}
						onPageHandler={handlePageChange}
						isLoading={isLoading}
						tableStyles={{ minWidth: '46.875em', maxHeight: '100vh' }}
						onSortHandler={handleSortChange}
						rowKey="groupId"
						customErrorMessage={errorMessage}
						enableHeaderColor
						isRenderPagination
					/>
				</Box>
			</Grid>
		</Box>
	);
};

export default CompleteTabTable;
